import { useEffect, useMemo, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { LedgerEntry, Recipient, Season } from "../../api/types";
import { UserPicker, type UserPickerValue } from "../../components/UserPicker";
import { formatDateTime } from "../../lib/format";
import { useIdempotencyKey } from "../../lib/idempotencyKey";

interface State {
  status: "loading" | "ready" | "error";
  activeSeason: Season | null;
  entries: LedgerEntry[];
  errorMessage: string | null;
}

interface FormState {
  target: UserPickerValue;
  amount: string;
  reason: string;
}

interface TargetsState {
  status: "loading" | "ready" | "error";
  targets: Recipient[];
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  activeSeason: null,
  entries: [],
  errorMessage: null
};

const initialForm: FormState = {
  target: { query: "", selectedID: "" },
  amount: "",
  reason: ""
};
const initialTargets: TargetsState = { status: "loading", targets: [], errorMessage: null };

export function Adjustments() {
  const [state, setState] = useState<State>(initial);
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [targetsState, setTargetsState] = useState<TargetsState>(initialTargets);
  const { keyFor, reset } = useIdempotencyKey();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const seasons = await apiClient.listSeasons({ status: "active", limit: 1 });
        const active = seasons.seasons[0] ?? null;
        if (active === null) {
          if (!cancelled) {
            setState({ status: "ready", activeSeason: null, entries: [], errorMessage: null });
          }
          return;
        }
        const ledger = await apiClient.listLedger({ seasonID: active.id, limit: 100 });
        if (cancelled) {
          return;
        }
        const adjustments = ledger.entries.filter((entry) => entry.entry_type === "manual_adjustment");
        setState({
          status: "ready",
          activeSeason: active,
          entries: adjustments,
          errorMessage: null
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({
          status: "error",
          activeSeason: null,
          entries: [],
          errorMessage: messageFor(caught)
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Adjustment targets are any active member in the tenant — fetched once
  // alongside the page load. The picker is the human-friendly entry point;
  // the form still sends the picked user_id to the backend.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .listAdjustmentTargets()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setTargetsState({
          status: "ready",
          targets: response.recipients,
          errorMessage: null
        });
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }
        setTargetsState({
          status: "error",
          targets: [],
          errorMessage: caught instanceof Error ? caught.message : "Could not load adjustment targets."
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const amountNumber = useMemo(() => Number.parseInt(form.amount, 10), [form.amount]);
  const formValid =
    form.target.selectedID !== "" &&
    Number.isFinite(amountNumber) &&
    amountNumber !== 0 &&
    form.reason.trim() !== "";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!formValid || submitting || state.activeSeason === null) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    const payload = {
      target_user_id: form.target.selectedID,
      amount: amountNumber,
      reason: form.reason.trim(),
      season_id: state.activeSeason.id
    };
    const idempotencyKey = keyFor(JSON.stringify(payload));
    const target = targetsState.targets.find((t) => t.id === form.target.selectedID);

    try {
      const entry = await apiClient.adjust(payload, idempotencyKey);
      // Adjust's INSERT...RETURNING doesn't include the user JOIN, so the
      // wire entry has target_user_email = null. Patch it from the picked
      // target so the optimistic table row shows an email until the next
      // refresh hits the JOIN-backed list path.
      const patched: LedgerEntry =
        target !== undefined && entry.target_user_email === null
          ? { ...entry, target_user_email: target.email }
          : entry;
      setState((previous) => ({ ...previous, entries: [patched, ...previous.entries] }));
      const targetLabel = target?.name.trim() !== "" ? target?.name : target?.email ?? "the recipient";
      setSubmitSuccess(`${formatSignedAmount(amountNumber)} coins ${amountNumber >= 0 ? "credited to" : "debited from"} ${targetLabel}.`);
      setForm(initialForm);
      reset();
    } catch (caught) {
      setSubmitError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  function formatSignedAmount(value: number): string {
    return value > 0 ? `+${value}` : String(value);
  }

  if (state.status === "loading") {
    return <p className="loading">Loading adjustments…</p>;
  }
  if (state.status === "error") {
    return <div className="card"><p>{state.errorMessage}</p></div>;
  }
  if (state.activeSeason === null) {
    return (
      <div className="card empty-state">
        <h2>No active season</h2>
        <p>Adjustments are recorded against an active season. Open one first.</p>
      </div>
    );
  }

  return (
    <div className="adjustments-view">
      <section className="card">
        <h2>Manual adjustment</h2>
        <p className="muted">Positive amounts grant coins. Negative amounts deduct (must not go below zero).</p>
        <form onSubmit={handleSubmit}>
          <UserPicker
            label="Target"
            placeholder="Type a name or email…"
            value={form.target}
            recipients={targetsState.targets}
            loadStatus={targetsState.status}
            loadErrorMessage={targetsState.errorMessage}
            loadingMessage="Loading the member roster…"
            emptyMessage="No other active members in your tenant yet."
            noSelectionHint="Pick a person from the list."
            required
            onChange={(next) => setForm((previous) => ({ ...previous, target: next }))}
          />
          <div className="form-row">
            <label>Amount (signed)</label>
            <input
              type="number"
              step={1}
              value={form.amount}
              onChange={(event) => setForm((previous) => ({ ...previous, amount: event.target.value }))}
              onFocus={(event) => event.currentTarget.select()}
              required
            />
          </div>
          <div className="form-row">
            <label>Reason</label>
            <textarea
              rows={2}
              value={form.reason}
              onChange={(event) => setForm((previous) => ({ ...previous, reason: event.target.value }))}
              required
            />
          </div>
          {submitError !== null && <p className="form-row__error">{submitError}</p>}
          {submitSuccess !== null && <p className="form-row__success" role="status">{submitSuccess}</p>}
          <button type="submit" className="btn btn--primary" disabled={!formValid || submitting}>
            {submitting ? "Recording…" : "Record adjustment"}
          </button>
        </form>
      </section>

      <section className="card">
        <h3>Recent adjustments</h3>
        {state.entries.length === 0 ? (
          <p className="muted">No manual adjustments yet this season.</p>
        ) : (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Target</th>
                <th>Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {state.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>{entry.target_user_email ?? "—"}</td>
                  <td>{entry.amount > 0 ? `+${entry.amount}` : entry.amount}</td>
                  <td>{entry.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "insufficient_balance") {
      return "Adjustment would result in a negative balance.";
    }
    if (caught.code === "invalid_target") {
      return "Target user is not an active member.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not record adjustment.";
}
